/**
 * Knob position (0..1) ↔ shader uniform value: THE mapping every knob path
 * uses (a drag, MIDI, randomize, a System Link peer, a feed preview, the
 * mixer's other channels), so they cannot disagree about ranges, ints, menus
 * or vector components. shaderParser.applyMappedKnobValue
 * delegate here.
 *
 * Per type:
 *  - ISF `long` with a menu: the entry the knob selects — the same entry the
 *    panel's menu label shows (knobDisplay.knobMenuLabel, through
 *    {@link knobMenuIndex}) — and its VALUE (entry index when the menu has
 *    LABELS but no VALUES).
 *  - ISF `event` and `bool`: a momentary / plain switch, on past the middle.
 *  - `int`: rounded through its range; without one, the parser's own int
 *    default (0..10), the range the knob's position is shown against.
 *  - `float` (and a split vector component): linear through its range (0..1
 *    without one).
 *  - a whole `vec2/3/4` (never produced by the parsers, which split vectors
 *    into components): every component at the position, alpha 1.
 */

import type { ShaderUniform } from './shaderParser';

export type KnobUniform = Pick<ShaderUniform, 'type' | 'range' | 'defaultValue' | 'knobDefault'>;
export type UniformValue = number | boolean | number[];

/** What the mapping reads from a uniform. */
export type MappedUniform = Pick<ShaderUniform, 'type' | 'range' | 'isfType' | 'values' | 'labels'>;

/** A material's uniform table, as three.js holds it. */
export type UniformTable = Record<string, { value: unknown } | undefined>;

/** `tint_x` → ['tint', 1]: a knob that drives one component of a vector uniform. */
const COMPONENT_RE = /^(.+)_([xyzw])$/;
const COMPONENTS = 'xyzw';

/** shaderParser's default range for an `int` declared without one. */
const INT_DEFAULT_RANGE: readonly [number, number] = [0, 10];

/** How many entries an ISF `long` menu has (0: not a menu). */
function menuLength(uniform: Pick<ShaderUniform, 'isfType' | 'values' | 'labels'>): number {
  if (uniform.isfType !== 'long') return 0;
  return uniform.values?.length || uniform.labels?.length || 0;
}

/**
 * The menu entry a knob position selects for an ISF `long` with VALUES or
 * LABELS, or -1 for any other input. Shared with knobDisplay.knobMenuLabel so
 * the label shown and the value written are always the same entry.
 */
export function knobMenuIndex(uniform: Pick<ShaderUniform, 'isfType' | 'values' | 'labels'>, value: number): number {
  const n = menuLength(uniform);
  if (n === 0) return -1;
  const v = Number.isFinite(value) ? value : 0;
  return Math.min(n - 1, Math.max(0, Math.floor(v * n)));
}

/** A scalar knob's value: what a single uniform (or one vector component) takes. */
function scalarValue(uniform: MappedUniform, value: number): number | boolean {
  const menu = knobMenuIndex(uniform, value);
  if (menu >= 0) return uniform.values?.[menu] ?? menu;
  if (uniform.isfType === 'event' || uniform.type === 'bool') return value > 0.5;
  if (uniform.type === 'int') {
    const [min, max] = uniform.range ?? INT_DEFAULT_RANGE;
    return Math.round(min + value * (max - min));
  }
  const [min, max] = uniform.range ?? [0, 1];
  return min + value * (max - min);
}

function vectorSize(type: ShaderUniform['type']): number {
  return type === 'vec2' ? 2 : type === 'vec3' ? 3 : type === 'vec4' ? 4 : 0;
}

/** Fills `out` (length = the vector's size) with a whole-vector knob's value. */
function fillVector(out: number[], value: number): void {
  for (let i = 0; i < out.length; i++) out[i] = i === 3 ? 1.0 : value;
}

/** A knob's 0..1 position → the value its uniform takes (a fresh array for a whole vector). */
export function uniformValueForKnob(uniform: MappedUniform, value: number): UniformValue {
  const size = vectorSize(uniform.type);
  if (size > 0) {
    const out = new Array<number>(size);
    fillVector(out, value);
    return out;
  }
  return scalarValue(uniform, value);
}

/**
 * Writes a knob's value into `uniforms`: the uniform of that name, or, for a
 * component knob (`tint_x`) whose parent vector is there, that component.
 * Vector values are written into the existing array in place (three.js
 * re-uploads `.value` every draw and each material owns its arrays), so a
 * knob drag allocates nothing. Returns the name it wrote, or null when the
 * table has neither (a stale mapping while a new shader compiles).
 */
export function applyKnobToUniforms(
  uniforms: UniformTable | null | undefined,
  uniform: Pick<ShaderUniform, 'name'> & MappedUniform,
  value: number,
): string | null {
  if (!uniforms) return null;
  const own = uniforms[uniform.name];
  if (own) {
    const size = vectorSize(uniform.type);
    if (size > 0 && Array.isArray(own.value) && own.value.length === size) fillVector(own.value as number[], value);
    else own.value = uniformValueForKnob(uniform, value);
    return uniform.name;
  }
  const match = COMPONENT_RE.exec(uniform.name);
  const parent = match ? uniforms[match[1]] : undefined;
  if (!match || !parent) return null;
  const index = COMPONENTS.indexOf(match[2]);
  const mapped = scalarValue(uniform, value);
  const component = typeof mapped === 'number' ? mapped : mapped ? 1 : 0;
  if (Array.isArray(parent.value) && parent.value.length > index) {
    (parent.value as number[])[index] = component;
  } else {
    // No vector there yet: start one sized to reach this component, the rest centred.
    const fresh = new Array<number>(Math.max(2, index + 1)).fill(0.5);
    fresh[index] = component;
    parent.value = fresh;
  }
  return match[1];
}

/** The parent uniform a knob writes to: `tint` for `tint_x`, else its own name. */
export function materialUniformName(name: string, uniforms?: UniformTable | null): string {
  if (uniforms?.[name]) return name;
  const match = COMPONENT_RE.exec(name);
  return match ? match[1] : name;
}

/**
 * The position a knob starts at, 0..1: its source default (PROTOCOL.md §3.2) -- the reader's `knobDefault` (an ISF
 * input's, the engine's rule: inputs.ts isfKnobDefault; a native shader's, the engine's own), else (a legacy GLSL
 * shader's knob) its DEFAULT through its range (the first component of a vector), clamped.
 */
export function defaultKnobPosition(uniform: KnobUniform): number {
  if (typeof uniform.knobDefault === 'number' && Number.isFinite(uniform.knobDefault)) {
    return Math.max(0, Math.min(1, uniform.knobDefault));
  }
  const raw = Array.isArray(uniform.defaultValue) ? uniform.defaultValue[0] : uniform.defaultValue;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0.5;
  const position = uniform.range && uniform.range[1] !== uniform.range[0]
    ? (raw - uniform.range[0]) / (uniform.range[1] - uniform.range[0])
    : raw;
  return Math.max(0, Math.min(1, position));
}

/**
 * INIT (beside the 🎲): every knob not locked back to its source default (defaultKnobPosition, PROTOCOL.md §3.2), by
 * its key -- never a position saved elsewhere (a post's). Locked knobs stay, as the 🎲 leaves them.
 */
export function initKnobPositions<K>(
  knobs: Iterable<{ key: K; locked: boolean; uniform: KnobUniform }>,
): Map<K, number> {
  const positions = new Map<K, number>();
  for (const knob of knobs) {
    if (knob.locked) continue;
    positions.set(knob.key, defaultKnobPosition(knob.uniform));
  }
  return positions;
}

/**
 * Randomize (the knob panel's 🎲, a mixer section title's): a fresh 0..1 position for every knob not locked, by its
 * key. Only positions: each goes through the mapping above like a drag's (an ISF menu lands on an entry, an `event`
 * or `bool` switches on past the middle, an `int` on a step), so randomizing follows every type's own rules.
 */
export function randomKnobPositions<K>(
  knobs: Iterable<{ key: K; locked: boolean }>, random: () => number = Math.random,
): Map<K, number> {
  const positions = new Map<K, number>();
  for (const knob of knobs) {
    if (knob.locked) continue;
    const value = random();
    positions.set(knob.key, Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0);
  }
  return positions;
}
